import React, { ReactElement, useState, MouseEvent } from 'react';
import { Box, IconButton, Menu, MenuItem, Paper } from '@material-ui/core';
import FolderIcon from '@material-ui/icons/Folder';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import VisibilityIcon from '@material-ui/icons/Visibility';
import CreateIcon from '@material-ui/icons/Create';
import DeleteIcon from '@material-ui/icons/Delete';
import { connect } from 'react-redux';
import {
	AppThunkDispatch,
	BosatlasAction,
	bosatlasDeleteBundle,
	Bundle
} from '../../../redux';
import { Header5, Body2 } from '../../atoms';

import './bundle-list-item.styles.scss';
import { useTranslation } from 'react-i18next';

export interface IBundleListItemProps {
	bundle: Bundle;
	disableEditing?: boolean | undefined;
	onEditClick: () => void;
	onViewContentClick: (bundleId: string) => void;
}

export interface IBundleListItemStateProps {
	deleteBundle: (id: string) => void;
}

function BundleListItemView(
	props: IBundleListItemProps & IBundleListItemStateProps
): ReactElement {
	const {
		bundle,
		disableEditing,
		onEditClick,
		deleteBundle,
		onViewContentClick
	} = props;

	const { t } = useTranslation('myBosatlas');

	const [anchorElement, setAnchorElement] =
		useState<HTMLElement | undefined>(undefined);
	const [selectedBundle, setSelectedBundle] =
		useState<Bundle | undefined>(undefined);

	function handleMoreClick(
		event: React.MouseEvent<HTMLButtonElement>,
		bundle: Bundle
	): void {
		event.stopPropagation();
		setSelectedBundle(bundle);
		setAnchorElement(event.currentTarget);
	}

	function handleMenuClose(): void {
		setAnchorElement(undefined);
		setSelectedBundle(undefined);
	}

	function handleEditNameClick(): void {
		onEditClick();
		handleMenuClose();
	}

	function handleDeleteClick(): void {
		deleteBundle(selectedBundle?.id as string);
		handleMenuClose();
	}

	function handleViewContentClick(): void {
		onViewContentClick(bundle.id as string);
	}

	function getCaptionText(): string {
		const amount: number = bundle.contentAmount ? bundle.contentAmount : 0;
		if (amount === 1) return t('folder.oneMap');
		if (amount > 1) return t('folder.amountMaps', { amount });
		return t('folder.noMaps');
	}

	return (
		<Paper
			className="bundle-item cursor-pointer"
			onClick={handleViewContentClick}
			elevation={0}
		>
			<Box className="icon-name-container">
				<FolderIcon className="bundle-icon" fontSize="large" />
				<Box className="bundle-item-name">
					<Header5>{bundle.title}</Header5>
					<Body2>{getCaptionText()}</Body2>
				</Box>
			</Box>
			<IconButton onClick={(event) => handleMoreClick(event, bundle)}>
				<MoreVertIcon className="bundle-item-more-icon" />
			</IconButton>
			<ItemPopupMenu
				anchorElement={anchorElement}
				disableEditing={disableEditing}
				handleMenuClose={handleMenuClose}
				onClickEditName={handleEditNameClick}
				onClickDelete={handleDeleteClick}
				onClickViewContent={handleViewContentClick}
			/>
		</Paper>
	);
}

function ItemPopupMenu({
	anchorElement,
	disableEditing,
	handleMenuClose,
	onClickEditName,
	onClickDelete,
	onClickViewContent
}: {
	anchorElement: Element | undefined;
	disableEditing?: boolean | undefined;
	handleMenuClose: () => void;
	onClickEditName: () => void;
	onClickDelete: () => void;
	onClickViewContent: () => void;
}): ReactElement {
	const { t } = useTranslation('myBosatlas');

	function onClick(event: MouseEvent<HTMLDivElement>): void {
		event.stopPropagation();
	}

	return (
		<Menu
			anchorEl={anchorElement}
			open={Boolean(anchorElement)}
			onClick={onClick}
			onClose={handleMenuClose}
			className="bundle-menu-popup"
		>
			<MenuItem
				className="bundle-menu-popup-item"
				onClick={onClickViewContent}
			>
				<VisibilityIcon color="primary" />
				{t('folder.openFolder')}
			</MenuItem>
			{!disableEditing && [
				<MenuItem
					className="bundle-menu-popup-item"
					key={'change-bundle-name'}
					onClick={onClickEditName}
				>
					<CreateIcon color="primary" />
					{t('folder.renameFolder')}
				</MenuItem>,
				<MenuItem
					className="bundle-menu-popup-item"
					key={'delete-bundle'}
					onClick={onClickDelete}
				>
					<DeleteIcon color="primary" />
					{t('folder.deleteFolder')}
				</MenuItem>
			]}
		</Menu>
	);
}

function mapDispatchToProps(dispatch: AppThunkDispatch<BosatlasAction>) {
	return {
		deleteBundle: (id: string) => dispatch(bosatlasDeleteBundle(id))
	};
}

export const BundleListItem = connect(
	undefined,
	mapDispatchToProps
)(BundleListItemView);
